import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getProducts, getCategories, getPickupLocations, addToWishlist, removeFromWishlist, getMyWishlist, API } from "@/lib/api";
import { getCurrency, fmtMoney } from "@/lib/currency";
import { Heart, Loader2, MapPin, Truck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function Shop() {
  const { user, login } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [wishlist, setWishlist] = useState(new Set());
  const [locations, setLocations] = useState([]);
  const [buying, setBuying] = useState(null); // product being checked out
  const [ship, setShip] = useState({ method: "ship", name: "", address: "", pickup_location_id: "" });
  const [busy, setBusy] = useState(false);
  const { code, symbol } = getCurrency();

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    getPickupLocations().then(setLocations).catch(() => {});
  }, []);
  useEffect(() => { getProducts(cat || undefined).then(setProducts).catch(() => {}); }, [cat]);
  useEffect(() => {
    if (user) getMyWishlist().then((w) => setWishlist(new Set(w.product_ids))).catch(() => {});
  }, [user]);

  const toggleWish = async (p) => {
    if (!user) { login(); return; }
    if (wishlist.has(p.id)) {
      await removeFromWishlist(p.id).catch(() => {});
      setWishlist((s) => { const n = new Set(s); n.delete(p.id); return n; });
    } else {
      await addToWishlist(p.id).catch(() => {});
      setWishlist((s) => new Set(s).add(p.id));
      toast.success("Saved to your list");
    }
  };

  const startCheckout = async () => {
    if (!buying) return;
    if (ship.method === "pickup" && !ship.pickup_location_id) { toast.error("Choose a pickup location"); return; }
    if (ship.method === "ship" && !ship.address.trim()) { toast.error("Enter your delivery address"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: "product",
        product_id: buying.id,
        origin_url: window.location.origin,
        currency: code,
        shipping_method: ship.method,
        shipping_name: ship.name,
        shipping_address: ship.address,
        pickup_location_id: ship.method === "pickup" ? ship.pickup_location_id : undefined,
      }, { withCredentials: true, headers: localStorage.getItem("rada_session") ? { Authorization: `Bearer ${localStorage.getItem("rada_session")}` } : {} });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="shop-page" className="min-h-screen bg-[#F4F1EC]">
      <header className="bg-[#1F3864] text-[#EBE6E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" data-testid="shop-home-link"><img src="/rada-logo-white.png" alt="Rada Collab" className="h-6" /></Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs font-bold uppercase tracking-wider text-[#EBE6E0]/70 hover:text-[#D7E85C]">Home</Link>
            {user ? (
              <Link to="/account" data-testid="shop-account-link" className="text-xs font-bold uppercase tracking-wider text-[#D7E85C]">My account</Link>
            ) : (
              <button data-testid="shop-login-btn" onClick={() => login()} className="text-xs font-bold uppercase tracking-wider text-[#D7E85C]">Sign in</button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-12">
        <h1 className="font-display font-black uppercase text-5xl md:text-7xl text-[#1F3864] mb-2">The Shop</h1>
        <p className="text-[#1F3864]/60 mb-8">Wearable art and goods, made in the Philippines.</p>

        <div className="flex flex-wrap gap-2 mb-10">
          <button data-testid="filter-all" onClick={() => setCat("")}
            className={`px-4 py-2 rounded-full text-sm font-bold ${!cat ? "bg-[#1F3864] text-[#D7E85C]" : "bg-white text-[#1F3864] border border-[#1F3864]/20"}`}>
            All
          </button>
          {categories.map((c) => (
            <button key={c.id} data-testid={`filter-${c.slug}`} onClick={() => setCat(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold ${cat === c.id ? "bg-[#1F3864] text-[#D7E85C]" : "bg-white text-[#1F3864] border border-[#1F3864]/20"}`}>
              {c.name}
            </button>
          ))}
        </div>

        {products.length === 0 ? (
          <p data-testid="shop-empty" className="text-center text-[#1F3864]/50 py-20">No products in this category yet. Check back soon.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} data-testid={`product-card-${p.id}`} className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-hidden group">
                <div className="relative">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.title} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-[#1F3864]/5 grid place-items-center text-[#1F3864]/30 font-display text-4xl font-black">
                      {p.title.charAt(0)}
                    </div>
                  )}
                  <button data-testid={`wish-${p.id}`} onClick={() => toggleWish(p)}
                    className={`absolute top-3 right-3 h-9 w-9 rounded-full grid place-items-center transition-colors ${
                      wishlist.has(p.id) ? "bg-[#FF8473] text-white" : "bg-white/90 text-[#1F3864] hover:text-[#FF8473]"}`}>
                    <Heart size={16} fill={wishlist.has(p.id) ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="p-5">
                  <div className="font-bold text-[#1F3864]">{p.title}</div>
                  <p className="text-sm text-[#1F3864]/60 line-clamp-2 mt-1 mb-3">{p.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-display font-black text-2xl text-[#1F3864]" data-testid={`price-${p.id}`}>
                      {fmtMoney(symbol, code === "php" ? p.price_php : p.price_usd)}
                    </span>
                    <button data-testid={`buy-${p.id}`} onClick={() => { setBuying(p); setShip((s) => ({ ...s, name: user?.name || s.name })); }}
                      className="bg-[#1F3864] text-[#D7E85C] font-bold text-sm px-4 py-2.5 rounded-full hover:bg-[#16294B]">
                      Buy now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Checkout modal */}
      {buying && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-[#1F3864]/60 backdrop-blur-sm p-4" onClick={() => !busy && setBuying(null)}>
          <div data-testid="checkout-modal" className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display font-bold text-xl text-[#1F3864] mb-1">{buying.title}</h2>
            <p className="text-[#1F3864]/60 text-sm mb-5">{fmtMoney(symbol, code === "php" ? buying.price_php : buying.price_usd)}</p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="method-ship" onClick={() => setShip({ ...ship, method: "ship" })}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold ${
                    ship.method === "ship" ? "border-[#1F3864] bg-[#1F3864] text-[#D7E85C]" : "border-[#1F3864]/20 text-[#1F3864]"}`}>
                  <Truck size={16} /> Ship to me
                </button>
                <button data-testid="method-pickup" onClick={() => setShip({ ...ship, method: "pickup" })}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold ${
                    ship.method === "pickup" ? "border-[#1F3864] bg-[#1F3864] text-[#D7E85C]" : "border-[#1F3864]/20 text-[#1F3864]"}`}>
                  <MapPin size={16} /> Pick up
                </button>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Your name</label>
                <input data-testid="ship-name" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })}
                  className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#1F3864]" />
              </div>

              {ship.method === "ship" ? (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Delivery address</label>
                  <textarea data-testid="ship-address" value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })}
                    rows={2} placeholder="Street, city, province, postal code"
                    className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#1F3864]" />
                </div>
              ) : (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Pick up at</label>
                  <select data-testid="pickup-select" value={ship.pickup_location_id} onChange={(e) => setShip({ ...ship, pickup_location_id: e.target.value })}
                    className="mt-1 w-full border border-[#1F3864]/20 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#1F3864] bg-white">
                    <option value="">Choose a location…</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ""}</option>)}
                  </select>
                </div>
              )}

              <button data-testid="checkout-pay-btn" onClick={startCheckout} disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#1F3864] text-[#D7E85C] font-bold py-3.5 rounded-full hover:bg-[#16294B] disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {busy ? "Redirecting…" : "Continue to secure checkout"}
              </button>
              <p className="text-center text-xs text-[#1F3864]/40">Secure checkout via Stripe</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
